import { FC, useMemo } from "react";
import { Nullable, useVehicleControllerDelete } from "service";

import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";

import { useNavigate } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "../../../constants/ad-routes";
import { AdminVehicleResDto } from "service/api/estabrookSchemas";
import { formatDateTime } from "../../../utils/string-helpers";
import { Check, Close } from "@mui/icons-material";
import InteractableCell from "../components/generic-table/interactable-cell";
import { toast } from "react-toastify";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import { useDialogStore } from "../../../state/dialog-store";

const sanitizedHeader = [
  {
    label: "License Plate",
    value: "licensePlate",
    sortable: true,
    size: "15%",
  },
  {
    label: "Type",
    value: "vehicleType",
    sortableValue: "typeName",
    sortable: true,
    size: "15%",
  },
  {
    label: "Availability",
    value: "available",
    sortable: true,
    size: "15%",
  },
  {
    label: "Last Plow",
    value: "lastPlow",
    sortable: true,
    sortableValue: "lastPlow",
    size: "15%",
  },
  {
    label: "Last Updated",
    value: "updatedAt",
    sortable: true,
    sortableValue: "updatedAt",
    size: "15%",
  },
  {
    label: "",
    value: "actions",
    size: "8%",
  },
];

export interface FleetManagementTableProps {
  data?: AdminVehicleResDto[];
  sortOptions: Nullable<SortOptions>;
  sortOptionsChange: (value: Nullable<SortOptions>) => void;
  isLoading: boolean;
}

const FleetManagementTable: FC<FleetManagementTableProps> = (props) => {
  const { data = [], sortOptions, sortOptionsChange, isLoading } = props;
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();

  const onVehicleDetail = (id: string) => {
    navigate(generateRoute(AdRoutes.editVehicle, { vehicleId: id }));
  };

  const { mutate: removeVehicle } = useVehicleControllerDelete({
    onSuccess: () => {
      toast.success("Vehicle Deleted Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles",
          operationId: "vehicleControllerGetAll",
          variables: {},
        }),
      );
    },
  });

  const onDeleteVehicle = (
    id: string,
    licensePlate: string,
    vehicleName: number,
  ) => {
    setDialog({
      title: "Delete Vehicle",
      description: `Are you sure you want to delete the vehicle ${licensePlate} - ${vehicleName}? This will remove it from all assigned routes and cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        removeVehicle({ pathParams: { vehicleId: id || "" } });
        onClose();
      },
    });
  };

  const renderRowLabel: Record<
    string,
    RenderGenericRowFunction<AdminVehicleResDto>
  > = useMemo(
    () => ({
      licensePlate: ({ headerValue, data: { licensePlate } }) => ({
        label: licensePlate ?? "Unassigned",
        value: headerValue,
      }),
      vehicleType: ({ headerValue, data: { vehicleType } }) => ({
        label: vehicleType?.typeName,
        value: headerValue,
      }),
      available: ({ headerValue, data: { available } }) => ({
        label: available ? <Check color="success" /> : <Close color="error" />,
        value: headerValue,
      }),
      travelSpeed: ({ headerValue, data: { travelSpeed } }) => ({
        label: `${travelSpeed} mph`,
        value: headerValue,
      }),
      lastPlow: ({ headerValue, data: { lastPlow } }) => ({
        label: lastPlow ? formatDateTime(lastPlow) : "No Plow",
        value: headerValue,
      }),
      updatedAt: ({ headerValue, data: { updatedAt } }) => ({
        label: formatDateTime(updatedAt, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        value: headerValue,
      }),
      actions: ({ headerValue, data }) => {
        const { id, licensePlate, vehicleType } = data;
        return {
          label: (
            <InteractableCell
              label={""}
              onDelete={() =>
                onDeleteVehicle(id, licensePlate, vehicleType?.id)
              }
              onEdit={() => onVehicleDetail(id)}
            />
          ),
          value: headerValue,
        };
      },
    }),
    [],
  );
  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabel}
      data={data}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

export default FleetManagementTable;
